/**
 * AppFooter — the app-shell footer band (design audit R5).
 * ---------------------------------------------------------------------------
 * The client portal had NO app-level footer. This slim, persistent band sits at
 * the bottom of the main column (spanning content + right rail), giving every
 * page a consistent frame: brand + version on the left, quick links on the
 * right. Compact (single row) so it never steals vertical space from content.
 */
import { Link } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { cn } from '@/lib/utils'

const YEAR = new Date().getFullYear()
const VERSION =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_APP_VERSION) || '2026.07'

const LINKS: { label: string; to: string }[] = [
  { label: 'Assistant IA', to: '/chat' },
  { label: 'Rapports', to: '/reports' },
  { label: 'Confidentialité', to: '/settings/preferences' },
  { label: 'Paramètres', to: '/settings' },
]

export function AppFooter({ className }: { className?: string }) {
  return (
    <footer
      className={cn(
        'shrink-0 border-t border-border/50 bg-[hsl(var(--topbar-bg))]/40 backdrop-blur',
        // Render at every width so the contentinfo landmark is present on phones
        // too (was hidden < sm). Secondary links stay hidden on the smallest sizes.
        // A11Y: the band bg is topbar-bg/40 (a translucent mid-tone), so muted
        // text fell to ~3:1. Use foreground/80 which clears 4.5:1 on the band.
        'flex h-9 items-center gap-3 px-4 text-[11px] text-foreground/80',
        className,
      )}
      aria-label="Pied de page"
    >
      <div className="flex items-center gap-1.5 font-semibold text-foreground/90">
        <Shield className="h-3.5 w-3.5 text-primary" />
        <span>SGRC · GovRisk</span>
      </div>
      <span className="text-foreground/80">© {YEAR}</span>
      <span className="hidden rounded-full bg-muted px-1.5 py-0.5 font-mono text-[9px] text-muted-foreground md:inline">
        v{VERSION}
      </span>

      <nav aria-label="Liens de pied de page" className="ml-auto flex items-center gap-3">
        {LINKS.map((l) => (
          <Link key={l.to} to={l.to} className="hidden font-medium transition-colors hover:text-foreground md:inline">
            {l.label}
          </Link>
        ))}
      </nav>
    </footer>
  )
}

export default AppFooter
